/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */
/**
 * File created by John M. Woodcock < john@userx.co >
 * on 8/16/2025
 * for userx-support
 */

import {useEffect, useState} from "react";
import supportAPI from "../../_apis/supportAPI";
import {search, sortObjects} from "../../_utilities/reactUtils";
import {fetchContact} from "../../_utilities/contactUtilities";
import Badge from "react-bootstrap/Badge";
import useDefaultsManager from "../../_hooks/useDefaultsManager";

const AdminCompany = () => {
    const [companyList, setCompanyList] = useState([]);

    useEffect(() => {
        supportAPI.get(`/company`).then(response => {
            if (response.data) {
                console.log("Fetched companies", response.data);
                setCompanyList(sortObjects(response.data, 'name'));
            }
        });
    }, []);

    const handleDelete = async (companyId) => {
        const ok = window.confirm(
            "This will delete the company, company contacts, company metadata permanently. Continue?"
        );
        if (!ok) return;

        try {
            await supportAPI.delete(`/company/${companyId}`);
            setCompanyList(prev => prev.filter(c => c.company_id !== companyId));
        } catch (e) {
            console.error("Delete failed", e);
            alert("Failed to delete company");
        }
    };

    return (
        <div className="component-container d-flex flex-column h-100">
            <h3>Manage Companies</h3>
            <p>Basically it is a list of the companies.</p>

            <div>
                {companyList.map(co => (
                    <CompanyRow key={co.company_id} company={co} onDelete={handleDelete}/>
                ))}
            </div>
        </div>
    );
};

function CompanyRow({company, onDelete}) {
    const mode = useDefaultsManager('mode-defaults');
    const [primary, setPrimary] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        mode.fetchDefaults();
    },[]);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                if (!alive) return;
                const rows = await search("company_contacts", [
                    `t.company_id = ${company.company_id}`,
                    `t.primary = 1`,
                ]);
                if (rows?.length) {
                    const contact = await fetchContact(rows[0].contact_id);
                    if (contact) {
                        setPrimary(contact || null);
                    }
                }
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [company.company_id]);

    const emailHref = primary?.email ? `mailto:${primary.email}` : undefined;
    const telHref = primary?.phone ? `tel:${primary.phone}` : undefined;

    const disabled = (href) => !href;

    const companyStatusId = company.meta.find(m => m.meta_key === 'status')?.meta_value;
    const companyStatus = mode.defaults.find(d => d.id.toString() === companyStatusId) || null;
    const statusIndex = companyStatus ? parseInt(companyStatus.id) - 1 : -1;
    const statusColor = ['success', 'primary', 'warning', 'danger'];

    return (
        <div className="alert border-opacity-10 border-dark py-2 mb-1 d-flex align-items-center">
            <h4 className="my-0 py-0 small flex-grow-1" title={company.company_id}>
                {company.name}
            </h4>
            <div type="info"
                   className={`text-${statusIndex >= 0 && statusIndex < statusColor.length ? statusColor[statusIndex] : 'secondary'} small ms-2`}>
                {companyStatus?.name ?? 'Unknown'}
            </div>

            <a className="d-block" href={`/clients/${company.company_id}`} title="Dashboard">
                <i role="button" className="bi bi-speedometer px-2"/>
            </a>

            <a className="d-block" href={`/clients/${company.company_id}/info`} title="Settings">
                <i role="button" className="bi bi-gear px-2"/>
            </a>

            {company.url && (
                <a className="d-block" href={company.url} target="_blank" rel="noreferrer" title="Visit Site">
                    <i role="button" className="bi bi-box-arrow-up-right px-2"/>
                </a>
            )}

            <a
                className={`d-block ${disabled(emailHref) ? "disabled opacity-50" : ""}`}
                href={emailHref || "#"}
                onClick={e => { if (disabled(emailHref)) e.preventDefault(); }}
                title={loading ? "Loading primary..." : (emailHref ? "Email Primary" : "No primary email")}
                target="_blank" rel="noreferrer"
            >
                <i role="button" className="bi bi-envelope-plus px-2"/>
            </a>

            <a
                className={`d-block ${disabled(telHref) ? "disabled opacity-50" : ""}`}
                href={telHref || "#"}
                onClick={e => { if (disabled(telHref)) e.preventDefault(); }}
                title={loading ? "Loading primary..." : (telHref ? "Call Primary" : "No primary phone")}
                target="_blank" rel="noreferrer"
            >
                <i role="button" className="bi bi-telephone-outbound px-2"/>
            </a>

            <a className="d-block" href={`/clients/${company.company_id}`} title="Delete" onClick={(e) => {
                e.preventDefault();
                onDelete(company.company_id);
            }}>
                <i role="button" className="bi bi-trash text-danger ms-3 px-1"/>
            </a>
        </div>
    );
}

export default AdminCompany;
